import React from "react"
import {
  Box,
  Typography,
  Divider as DividerMui,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid2
} from "@mui/material"
import { styled, ThemeProvider } from "@mui/material/styles"
import { Conditional, Stack, QRCodeWithIcon } from "src/shared/components"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import systemLogo from "src/assets/images/Logo-2.png"
import receiptTheme from "src/theme/receipt-theme"
import { DEFAULT_ORDER_RECEIPT_TITLE, OrderReceiptBaseProps } from ".."
import { QRCodeSVG } from "qrcode.react"
import WhatsAppIcon from "@mui/icons-material/WhatsApp"

import fbrPos from "src/assets/images/fbr-pos.jpg"
import { useTypedSelector } from "src/shared/store/store"
// Styled components for the receipt
const ReceiptContainer = styled(Box)(({ theme }) => ({
  width: "80mm", // Standard thermal receipt width
  margin: "0 auto",
  padding: theme.spacing(1),
  backgroundColor: "#fff",
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  "@media print": {
    width: "100%",
    height: "100%",
    overflow: "visible",
    breakInside: "avoid",
    pageBreakInside: "avoid",
    padding: theme.spacing(1),
    marginTop: 0
  }
}))

const ReceiptHeader = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(2),
  "@media print": {
    breakInside: "avoid",
    pageBreakInside: "avoid"
  }
}))

const ReceiptTitle = styled(Typography)(() => ({
  fontWeight: "bold"
  // marginBottom: theme.spacing(1)
}))

const ReceiptSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "@media print": {
    breakInside: "avoid",
    pageBreakInside: "avoid"
  }
}))

const CompactTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(0.4),
  fontSize: "0.75rem"
}))

const TotalRow = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  // marginTop: theme.spacing(1),
  padding: theme.spacing(0.3, 0)
}))
const Divider = styled(DividerMui)(() => ({
  border: "1px solid",
  borderColor: "#000"
}))
const DividerDotted = styled(DividerMui)(() => ({
  border: `1px dashed #000`
}))

// Main Receipt Component
const OrderReceiptTemplate1: React.FC<OrderReceiptBaseProps> = ({
  receiptData,
  receiptRef,
  isHideFooter = false,
  ...rest
}) => {
  const auth = useTypedSelector((state) => state?.authentication)
  const showMpWabaQr = ["mp", "mp-uat"].includes(auth?.tenant?.accountId ?? "  ")
  const wabaQr = (
    <QRCodeWithIcon
      value="http://wa.me/+923200446767"
      size={80}
      icon={
        <WhatsAppIcon
          sx={{
            fontSize: 12,
            color: "#000"
          }}
        />
      }
    />
  )
  // Calculate total quantity
  const totalQuantity = receiptData?.items?.reduce((sum, item) => sum + (item?.quantity || 0), 0) || 0

  return (
    <>
      <ThemeProvider theme={receiptTheme}>
        <ReceiptContainer ref={receiptRef} {...rest}>
          {/* Header Section */}

          <ReceiptHeader>
            {receiptData?.logo && <img style={{ width: "50%", height: "auto" }} src={receiptData.logo} />}

            {receiptData?.companyName && (
              <Typography variant="h6" fontWeight="bold">
                {receiptData?.companyName}
              </Typography>
            )}
            <Conditional condition={["mp", "mp-uat"].includes(auth?.tenant?.accountId ?? "  ")}>
              {receiptData?.websiteUrl && (
                <Typography variant="subtitle1">
                  {receiptData?.websiteUrl?.replace("www.", "")} | 0300 0446767
                </Typography>
              )}
            </Conditional>
            <Divider sx={{ borderWidth: "1px" }} />

            <ReceiptTitle variant="subtitle1">
              {receiptData?.title ?? DEFAULT_ORDER_RECEIPT_TITLE}
              {receiptData?.isDuplicateSlip ? (
                <>
                  {"-"}
                  <span
                    style={{ border: "1px dashed #000", borderRadius: "6px", padding: "2px 4px", marginLeft: "4px" }}
                  >
                    Duplicate
                  </span>
                </>
              ) : (
                <></>
              )}
            </ReceiptTitle>
            <Divider />
            {receiptData?.orderNumber && (
              <Stack variant="rowCenter" justifyContent={"space-evenly"} py="2px" spacing={1}>
                <Typography variant="subtitle2">Order #: {receiptData?.orderNumber}</Typography>{" "}
                <Divider flexItem orientation="vertical" />
                <Typography variant="subtitle2">{receiptData?.orderDate}</Typography>
              </Stack>
            )}

            <Divider />
            <Grid2 container spacing={0.5}>
              {/* row 1 */}
              <Grid2 size={{ xs: 6 }}>
                <Typography variant="subtitle2" textAlign={"left"}>
                  Customer: {receiptData?.customerName}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography variant="subtitle2" textAlign={"left"}>
                  Branch: {receiptData?.locationName}
                </Typography>
              </Grid2>
              {/* row 2 */}
              <Grid2 size={{ xs: 6 }}>
                <Typography variant="subtitle2" textAlign={"left"}>
                  Cell #: {receiptData?.customerPhone}
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography variant="subtitle2" textAlign={"left"}>
                  Order Type: {receiptData?.orderType}
                </Typography>
              </Grid2>
              {/* row 3 */}
              <Grid2 size={{ xs: 6 }}>
                {receiptData?.mrNo ? (
                  <Typography variant="subtitle2" textAlign={"left"}>
                    MR #: {receiptData?.mrNo}
                  </Typography>
                ) : (
                  <></>
                )}
              </Grid2>
              <Grid2 size={{ xs: 6 }}>
                <Typography variant="subtitle2" textAlign={"left"}>
                  Cashier: {receiptData?.cashierName}
                </Typography>
              </Grid2>
              {receiptData?.customerAddress && (
                <>
                  <Grid2 size={{ xs: 12 }}>
                    <Typography variant="subtitle2" textAlign={"left"}>
                      Customer Address: {receiptData.customerAddress}
                    </Typography>
                  </Grid2>
                </>
              )}
            </Grid2>
          </ReceiptHeader>

          {/* Items Section */}
          {receiptData?.items && receiptData?.items?.length > 0 && (
            <ReceiptSection>
              <TableContainer component={Box}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <CompactTableCell sx={{ p: 0 }} colSpan={5}>
                        <Divider />
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell
                        sx={{
                          fontWeight: 700,
                          textAlign: "center"
                        }}
                      >
                        QTY
                      </CompactTableCell>
                      <CompactTableCell
                        sx={{
                          fontWeight: 700,
                          textAlign: "center"
                        }}
                      >
                        Item
                      </CompactTableCell>
                      <CompactTableCell
                        sx={{
                          fontWeight: 700,
                          textAlign: "center"
                        }}
                      >
                        GST
                      </CompactTableCell>
                      <CompactTableCell
                        sx={{
                          fontWeight: 700,
                          textAlign: "center"
                        }}
                      >
                        Price
                      </CompactTableCell>
                      <CompactTableCell
                        sx={{
                          fontWeight: 700,
                          textAlign: "center"
                        }}
                      >
                        Total
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell sx={{ p: 0 }} colSpan={5}>
                        <Divider />
                      </CompactTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {receiptData?.items?.map((item, index) => (
                      <React.Fragment key={index}>
                        <TableRow>
                          <CompactTableCell sx={{ pl: 0, fontWeight: 700 }}>
                            {`${item?.quantity || 1}x`}
                          </CompactTableCell>
                          <CompactTableCell sx={{ pl: 0, fontWeight: 700 }}>
                            {` ${item?.name || "Product"}`}
                          </CompactTableCell>
                          <CompactTableCell align="center" sx={{ fontWeight: 700 }}>
                            {getDisplayPrice(item?.taxAmount ?? 0) || 0}
                          </CompactTableCell>
                          <CompactTableCell sx={{ pr: 0, fontWeight: 700 }} align="center">
                            {getDisplayPrice(item?.unitPrice ?? 0) || 0}
                          </CompactTableCell>
                          <CompactTableCell sx={{ pr: 0, fontWeight: 700 }} align="center">
                            {getDisplayPrice((item?.unitPrice ?? 0) * (item?.quantity ?? 0)) || 0}
                          </CompactTableCell>
                        </TableRow>

                        <Conditional condition={index < (receiptData.items?.length ?? 0) - 1}>
                          <TableRow>
                            <CompactTableCell sx={{ p: 0, height: "2px" }} colSpan={5}>
                              <DividerDotted />
                            </CompactTableCell>
                          </TableRow>
                        </Conditional>
                      </React.Fragment>
                    ))}
                    <TableRow>
                      <CompactTableCell colSpan={5}>
                        <Divider />
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "right" }} colSpan={2}>
                        SUBTOTAL
                      </CompactTableCell>
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                        {receiptData?.tax}
                      </CompactTableCell>
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                        {/* {receiptData?.subtotal ?? 0} */}
                      </CompactTableCell>
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                        {getDisplayPrice((receiptData?.subtotal ?? 0) + (receiptData?.tax ?? 0))}
                      </CompactTableCell>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell colSpan={3}>
                        <Stack direction={"row"} width={"100%"} spacing={0.5}>
                          <Typography component="p" variant="subtitle2">
                            Total Item(s): {receiptData?.items?.length}
                          </Typography>

                          <Typography component="p" variant="subtitle2">
                            Total QTY: {totalQuantity}
                          </Typography>
                        </Stack>
                      </CompactTableCell>
                      <Conditional condition={!!receiptData?.totalDiscount && receiptData?.totalDiscount > 0}>
                        <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>Discount</CompactTableCell>
                        <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                          {getDisplayPrice(receiptData?.totalDiscount ?? 0)}
                        </CompactTableCell>
                      </Conditional>
                    </TableRow>
                    <TableRow>
                      <CompactTableCell colSpan={3} />
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>Total</CompactTableCell>
                      <CompactTableCell sx={{ fontWeight: 700, textAlign: "center" }}>
                        {getDisplayPrice(
                          (receiptData?.subtotal ?? 0) + (receiptData?.tax ?? 0) - (receiptData?.totalDiscount ?? 0)
                        )}
                      </CompactTableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              <Divider />
            </ReceiptSection>
          )}

          {/* Summary Section */}
          {/* <ReceiptSection> */}
          {receiptData?.shippingFee ? (
            <TotalRow>
              <Typography variant="subtitle1">Shipping Fee:</Typography>
              <Typography variant="subtitle1">{getDisplayPrice(receiptData?.shippingFee ?? 0)}</Typography>
            </TotalRow>
          ) : (
            <></>
          )}
          {
            receiptData?.orderPlatformFees?.length ? (
              receiptData?.orderPlatformFees?.map((fee) => (
                <Grid2 key={fee?.name} container spacing={1} sx={{ width: "100%" }}>
                  <Grid2 size={10.5}>
                    <Typography variant="subtitle1" >{fee?.name}:</Typography>
                  </Grid2>
                  <Grid2 size={1.5}>
                    <Typography variant="subtitle1" textAlign="right">
                      {getDisplayPrice(fee?.amount ?? 0)}
                    </Typography>
                  </Grid2>
                </Grid2>
              ))
            ) : receiptData?.platformFee ? (
              <TotalRow>
                <Typography variant="subtitle1">Platform Fee:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(receiptData?.platformFee ?? 0)}</Typography>
              </TotalRow>
            ) : (
              <></>
            )
          }

          {receiptData?.integrations?.length ? (
            receiptData?.integrations?.map((integration) => (
              <TotalRow>
                <Typography variant="subtitle1">{integration?.name}:</Typography>
                <Typography variant="subtitle1">{getDisplayPrice(integration?.charges ?? 0)}</Typography>
              </TotalRow>
            ))
          ) : (
            <></>
          )}

          {receiptData?.total !== undefined && (
            <TotalRow sx={{ fontWeight: "bold", pt: 1 }}>
              <Typography variant="body2" fontWeight={700}>
                Total -GST Inclusive
              </Typography>
              <Typography variant="body2" fontWeight={700}>
                {getDisplayPrice(receiptData.total ?? 0, "PKR ")}
              </Typography>
            </TotalRow>
          )}
          <Divider />
          {receiptData?.paymentMethod && (
            <TotalRow>
              <Typography variant="subtitle2">Payment Method:</Typography>
              <Typography variant="subtitle2">{receiptData?.paymentMethod}</Typography>
            </TotalRow>
          )}

          {/* {receiptData.paymentMethod && (
              <Typography variant="subtitle1">
                {receiptData.paymentMethod} {receiptData.transactionNumber && `Tx no: ${receiptData.transactionNumber}`}
              </Typography>
            )} */}
          {/* </ReceiptSection> */}
          {/* Footer Section */}
          <ReceiptSection sx={{ textAlign: "center" }}>
            {receiptData?.specialInstructions && (
              <Typography variant="subtitle2" textAlign="left" gutterBottom>
                Special Instructions: {receiptData.specialInstructions}
              </Typography>
            )}

            <Conditional condition={!isHideFooter}>
              <Typography variant="subtitle2" gutterBottom>
                Visit us again soon!
              </Typography>
              {receiptData?.showOrderNumberQr && receiptData?.orderNumber ? (
                <Stack mt={1} direction="row" justifyContent="center" alignItems="flex-start" spacing={2}>
                  <Stack alignItems="center" spacing={0.5}>
                    {wabaQr}
                  </Stack>
                  <Stack alignItems="center" spacing={0.5}>
                    <QRCodeSVG value={receiptData.orderNumber} size={80} />
                    <Typography textAlign="center" variant="subtitle2">
                      {receiptData.orderNumber}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                showMpWabaQr && wabaQr
              )}
            </Conditional>

            {receiptData?.fbrInvoiceId && (
              <>
                <Stack mt={2} direction="row" justifyContent={"center"} spacing={1}>
                  <img src={fbrPos} width={80} height={80} alt="-" />
                  <QRCodeSVG value={`${receiptData.fbrInvoiceId}`} size={80} />
                </Stack>
                <Typography mt={0.5} textAlign={"center"} variant="subtitle2">
                  {receiptData?.fbrInvoiceId}
                </Typography>
              </>
            )}

            {/* {receiptData?.integrationPartnerInvoice !== undefined && (
              <Typography variant="caption" display="block" sx={{ mt: 2 }}>
                Integration Partner Invoice Number: {receiptData.integrationPartnerInvoice}
              </Typography>
            )} */}
            <Conditional condition={!isHideFooter}>
              <Stack my={1} spacing={0.5}>
                <Divider />
                <Divider />
              </Stack>
              <Stack variant="columnCenter" mt={0.5}>
                <Typography variant="subtitle2" display="block">
                  Powered by
                </Typography>
                <img src={systemLogo} width="100px" />
              </Stack>

              <Typography variant="subtitle2" display="block">
                www.three-shelves.com
              </Typography>
            </Conditional>
          </ReceiptSection>
        </ReceiptContainer>
      </ThemeProvider>
    </>
  )
}

export default OrderReceiptTemplate1
